// LARGE COMPONENT
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { REPORT_INFO_TYPES, REPORT_STATUS } from 'constants/reports';
import dayjs from 'dayjs';
import { useUIDispatchContext } from 'providers/UI';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useReportValuesDispatchContext,
  useReportValuesStateContext
} from 'providers/ReportValues';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { setTour } from 'store/tour/slice';
import { useAppDispatch } from 'store/hooks';
import {
  ReportStatus,
  createReport as createReportRest,
  fetchReportById,
  updateReport as updateReportRest
} from 'services/api/reports';
import { fetchPaymentAccount } from 'services/api/profile';

import { Loading, PageWrapper } from 'components/ui';
import DoctorInfo from './components/DoctorInfo';
import LivePreview from './components/LivePreview';
import PatientInfo from './components/PatientInfo';
import { showRestError } from 'utils/showErrorMessage';
import { HowItWorksLink } from 'components/shared';

const CreateUpdateReport: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleModal } = useUIDispatchContext();
  const params = useParams();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const appointmentId = searchParams.get('id');

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(!!params.id);
  const [saving, setSaving] = useState(false);

  const values = useReportValuesStateContext();
  const { setValues } = useReportValuesDispatchContext();

  const [signature, setSignature] = useState('');

  const finishLoading = useCallback(() => {
    setLoading(false);
    setTimeout(() => {
      dispatch(setTour({ key: 'addReport', value: true }));
    });
  }, [dispatch]);

  const getPaymentAccount = useCallback(() => {
    fetchPaymentAccount()
      .then(account => {
        if (account?.signature) {
          setSignature(account.signature);
        }
      })
      .catch(() => {
        // signature stays empty — same as legacy onError-less lazy query
      })
      .finally(() => finishLoading());
  }, [finishLoading]);

  useEffect(() => {
    if (!params.id) {
      getPaymentAccount();

      return;
    }

    fetchReportById(params.id)
      .then(reportData => {
        setValues({
          date_of_birth: dayjs(reportData.dateOfBirth),
          report: reportData.report,
          last_name: reportData.lastName,
          report_date: dayjs(reportData.dateReport),
          email: reportData.email,
          doctor_info: reportData.doctorInfo,
          first_name: reportData.firstName,
          lang: reportData.lang,
          icd: reportData.icd || []
        });

        if (reportData.signature) {
          setSignature(reportData.signature);
          finishLoading();
        } else {
          getPaymentAccount();
        }
      })
      .catch(err => showRestError(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const input = useMemo(
    () => ({
      dateOfBirth: values.date_of_birth
        ? dayjs(values.date_of_birth).format()
        : undefined,
      email: values.email || undefined,
      firstName: values.first_name,
      lastName: values.last_name,
      report: values.report,
      dateReport: values.report_date
        ? dayjs(values.report_date).format()
        : undefined,
      doctorInfo: values.doctor_info,
      lang: values.lang,
      signature: signature,
      icd: values.icd
    }),
    [signature, values]
  );

  const onSave = useCallback(
    (type: ReportStatus) => {
      setSaving(true);

      // appointment id is now an int (REST) — the appointment-bound create
      // goes through POST /reports with appointmentId, like the personal one
      const request = params.id
        ? updateReportRest(params.id, { ...input, status: type })
        : createReportRest({
            ...input,
            status: type,
            ...(appointmentId ? { appointmentId: Number(appointmentId) } : {})
          });

      request
        .then(() => {
          navigate(appointmentId ? '/report/medicus' : '/report/personal');
        })
        .catch(err => showRestError(err))
        .finally(() => setSaving(false));
    },
    [appointmentId, input, navigate, params.id]
  );

  const onSaveDraft = onSave;

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <PatientInfo
            signature={signature}
            appointmentId={appointmentId as string}
            loading={saving}
            onSignChange={setSignature}
            onSaveDraft={() => onSaveDraft(REPORT_STATUS.draft)}
            onClickNext={() => setStep(1)}
          />
        );
      default:
        return (
          <DoctorInfo
            loading={saving}
            onClickBack={() => setStep(0)}
            onSave={() => onSave(REPORT_STATUS.sent)}
            onSaveDraft={() => onSaveDraft(REPORT_STATUS.draft)}
          />
        );
    }
  };

  const onClickPreview = () => {
    toggleModal('reportPreview', true, {
      date: dayjs(values?.report_date).format() as string,
      aboutDoctor: values?.doctor_info as string,
      report: values?.report as string,
      patientBirth: dayjs(values?.date_of_birth).format() as string,
      patientName: values?.first_name as string,
      patientLastName: values?.last_name as string,
      appointmentId: appointmentId as string,
      signature,
      lang: values.lang,
      icd: values.icd
    });
  };

  if (loading || (params.id && !Object.values(values).some(item => item))) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      title={t(`reports.add_report`)}
      style={{ padding: 0 }}
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.REPORT.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <Row style={{ height: '100%' }}>
        <Col span={12} style={{ backgroundColor: 'var(--white)' }}>
          {renderStep()}
        </Col>
        <Col span={12} style={{ backgroundColor: '#F6F7FB' }}>
          <LivePreview
            type={
              step === 0 ? REPORT_INFO_TYPES.patient : REPORT_INFO_TYPES.doctor
            }
            content={
              step === 0 ? values?.report || '' : values.doctor_info || ''
            }
            signature={signature}
            appointmentId={appointmentId}
            step={step}
            onClickPreview={onClickPreview}
          />
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default CreateUpdateReport;
